import { Component, OnInit, OnDestroy, HostBinding } from '@angular/core';
import { Router, NavigationEnd, Event } from '@angular/router';
import { RouteService } from 'src/app/shared/services/route.service';
import { Subscription } from 'rxjs';
import { NotFoundSnackbarComponent } from 'src/app/shared/components/not-found-snackbar/not-found-snackbar.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { OfflineService } from 'src/app/shared/services/offline/offline.service';
import { HttpFileDownloadService } from 'src/app/shared/services/http-progress-event/http-file-download.service';
import { HttpEventType } from '@angular/common/http';
import { GeneralInfoLinks } from './search-bar-links';
import { RedirectService } from 'src/app/shared/services/redirect.service';
import { PlatformService } from 'src/app/shared/services/offline/platform.service';
import { EnvironmentService } from 'src/app/shared/services/environment.service';

@Component({
    selector: 'bell-search-bar',
    templateUrl: './search-bar.component.html',
    styleUrls: ['./search-bar.component.scss'],
    standalone: false
})

export class SearchBarComponent implements OnInit, OnDestroy {
  public dropdownCollapse = true;
  public currentModel: string;
  public searchQuery: string;

  public generalInfoLinks = GeneralInfoLinks;

  public appStoreLink: string;

  private routerSubscription: Subscription;

  @HostBinding('class.show-app-store-badge') showAppStoreBadge = false;

  constructor(
    public httpFileDownloadService: HttpFileDownloadService,
    public offlineService: OfflineService,
    private routeService: RouteService,
    private router: Router,
    private snackBar: MatSnackBar,
    private redirectService: RedirectService,
    private platformService: PlatformService,
    private environmentService: EnvironmentService
  ) { }

  ngOnInit() {
    this.trackRoute();
    this.routerSubscription = this.router.events.subscribe(
      (event: Event) => {
        if (event instanceof NavigationEnd) {
          this.trackRoute();
        }
      }
    );
    
    this.updateAppStoreBadgeVisibility();
    this.appStoreLink = this.showAppStoreBadge ? this.environmentService.getAppStoreUrl() : null;
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  trackRoute(): void {
    this.currentModel = this.routeService.getParameter('model');
  }

  search(): void {
    if (this.searchQuery && this.searchQuery.trim() !== '') {
      this.searchQuery = this.searchQuery.trim();
      this.redirectService.reSetCurrentSearchPageIndex();
      // if we are on a model page, use this model as a search filter
      const model = this.currentModel ? this.currentModel : 'ALL';
      this.router.navigate(['search'], {
        queryParams: { q: this.searchQuery, option: 'contains', model: model }
      });
    }
  }

  openPDF(link: any) {
    if (!this.dropdownCollapse) {
      this.toggleDropdown();
    }

    if (link) {
      this.httpFileDownloadService.getPDF(link.path, link.label).subscribe(
        (data) => {
          if (data && data.type === HttpEventType.Response) {
            return data;
          }
        },
        (error) => {
          this.snackBar.openFromComponent(NotFoundSnackbarComponent, {
            data: 'Error - Unable to retrieve PDF file.'
          });
        }
      );
    }
  }

  toggleDropdown() {
    this.dropdownCollapse = !this.dropdownCollapse;
  }

  onResize(event) {
    if (!this.dropdownCollapse) {
      this.toggleDropdown();
    }
  }

  private detectIOS(): boolean {
    // feature detection
    const supportsWebkitOverflowScroll = CSS.supports && (CSS.supports('-webkit-overflow-scrolling', 'auto') || CSS.supports('-webkit-touch-callout', 'none'));
    const hasTouchSupport = 'ontouchstart' in window || navigator.maxTouchPoints > 0; 
    const detectedIOSFeatures = supportsWebkitOverflowScroll && hasTouchSupport;

    // UA sniffing
    const userAgent = navigator.userAgent.toLowerCase();
    const platform = navigator.platform?.toLowerCase();
    const sniffedIOS = /ipad|iphone|ipod/.test(userAgent) ||
      /ipad|iphone|ipod/.test(platform) ||
      (platform === 'macintel' && navigator.maxTouchPoints > 1); // iPad Pro with iOS 13+
    
    return detectedIOSFeatures && sniffedIOS;
  }

  private detectSafari(): boolean {
    // detecting browser types on iOS is difficult due to frequently changing user agent strings and Apple requiring all browsers to use the WebKit engine
    // feature detection
    const hasWebKit = 'webkitAppearance' in document.documentElement.style;
    const hasApplePayAPI = 'ApplePaySession' in window;
    const hasChrome = 'chrome' in window;
    const hasOpera = 'opr' in window || 'opera' in window;
    const hasFirefox = 'InstallTrigger' in window;
    const hasEdge = 'msWriteProfilerMark' in window || 'MSStream' in window;
    const detectedSafariFeatures = hasWebKit && hasApplePayAPI && !hasChrome && !hasOpera && !hasFirefox && !hasEdge;

    // UA sniffing
    const userAgent = navigator.userAgent.toLowerCase();
    const sniffedSafari = /safari/.test(userAgent) && !/chrome|chromium|crios|edg|firefox|fxios|opera/.test(userAgent);
  
    return detectedSafariFeatures && sniffedSafari;    
  }

  private detectMobileDevice(): boolean {
    // feature detection
    const hasTouchSupport = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const hasOrientationAPI = 'orientation' in window || 'onorientationchange' in window;
    const hasMobileAPIs = (
      'DeviceMotionEvent' in window ||
      'DeviceOrientationEvent' in window ||
      'TouchEvent' in window
    );
    const detectedMobileFeatures = hasTouchSupport && hasOrientationAPI && hasMobileAPIs;

    // UA sniffing
    const userAgent = navigator.userAgent.toLowerCase();
    const sniffedMobile =
      /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent) ||
      (navigator.maxTouchPoints > 0 && /android|linux|windows/i.test(navigator.platform || '')) ||
      /mobile|tablet/i.test(userAgent);

    return detectedMobileFeatures && sniffedMobile;
  }

  private updateAppStoreBadgeVisibility(): void {
    const isOfflineCapable = this.platformService.isOfflineCapable();
    const isSafari = this.detectSafari();
    const isIOS = this.detectIOS();
    const isMobileDevice = this.detectMobileDevice();

    // Show badge if:
    // 1. Not offline capable AND non-safari AND iOS mobile device, (e.g. Chrome browser on iPad) OR
    // 2. Not offline capable AND non-iOS desktop device (e.g. Edge browser on desktop, Safari browser on Mac)
    this.showAppStoreBadge = !isOfflineCapable && ((!isSafari && isIOS && isMobileDevice) || (!isIOS && !isMobileDevice));

  }
}
